import * as React from 'react'; // eslint-disable-line
import styled from 'styled-components';

import { COLOR, FONT_SIZE, SPACING } from '../../packages/constants';
import { HelperModal, QuestionMark } from '../helper-modal';

export interface RadioInputProps {
    dataId: string;
    id: string;
    label: string;
    name: string;
    value: string;
    autofocus?: boolean;
    checked?: boolean;
    disabled?: boolean;
    hasHelper?: boolean;
    helperContent?: React.ReactNode;
    inline?: boolean;
    invalid?: boolean;
    onChange?: (e: any) => void;
    thinLabel?: boolean;
    validationMessage?: string;
}

interface StyledTextLabelProps {
    disabled?: boolean;
    invalid?: boolean;
    inline?: boolean;
    thinLabel?: boolean;
}

interface WrapperLabelProps {
    inline?: boolean;
}

const Wrapper = styled.label<WrapperLabelProps>`
    display: inline-flex;
    flex-direction: ${(props: WrapperLabelProps): string => (props.inline ? 'row-reverse' : 'column')};
    align-items: ${(props: WrapperLabelProps): string => (props.inline ? 'center' : '')};
    justify-content: ${(props: WrapperLabelProps): string => (props.inline ? 'flex-end' : '')};
`;

const WrapperHasHelper = styled.div<WrapperLabelProps>`
    display: inline-flex;
    flex-direction: ${(props: WrapperLabelProps): string => (props.inline ? 'row-reverse' : 'column')};
    align-items: ${(props: WrapperLabelProps): string => (props.inline ? 'center' : '')};
    justify-content: ${(props: WrapperLabelProps): string => (props.inline ? 'flex-end' : '')};
`;

const StyledLabelWrapper = styled.div`
    display: inline-flex;
`;

const StyledTextLabel = styled.span<StyledTextLabelProps>`
    display: block;
    margin-left: ${(props: StyledTextLabelProps): string => (props.inline ? SPACING.X_SMALL : '0')};
    font-size: ${FONT_SIZE.SMALL};
    line-height: ${FONT_SIZE.MEDIUM};
    font-weight: ${(props: StyledTextLabelProps): string => (props.thinLabel ? '400' : '700')};
    color: ${(props: StyledTextLabelProps): string => (props.invalid ? COLOR.PRIMARY.RED : COLOR.MAIN.LABEL_TEXT)};
    opacity: ${(props: StyledTextLabelProps): string => (props.disabled ? '0.7' : '1')};
`;

const StyledLabelHasHelper = styled.label<StyledTextLabelProps>`
    display: inline-block;
    margin-right: ${SPACING.X_SMALL};
    margin-left: ${(props: StyledTextLabelProps): string => (props.inline ? SPACING.X_SMALL : '0')};
    font-size: ${FONT_SIZE.SMALL};
    line-height: ${FONT_SIZE.MEDIUM};
    font-weight: ${(props: StyledTextLabelProps): string => (props.thinLabel ? '400' : '700')};
    color: ${(props: StyledTextLabelProps): string => (props.invalid ? COLOR.PRIMARY.RED : COLOR.MAIN.LABEL_TEXT)};
    opacity: ${(props: StyledTextLabelProps): string => (props.disabled ? '0.7' : '1')};
`;

const StyledRadio = styled.input``;

export const RadioInput = React.forwardRef<HTMLInputElement, RadioInputProps>(
    (
        {
            onChange,
            dataId,
            label,
            id,
            hasHelper,
            helperContent,
            name,
            value,
            autofocus,
            inline,
            thinLabel,
            checked,
            disabled,
            invalid,
            ...props
        },
        ref,
    ) => {
        return hasHelper ? (
            <WrapperHasHelper inline={inline} {...props}>
                <StyledLabelWrapper>
                    <StyledLabelHasHelper
                        data-test-id={`${dataId}-label`}
                        disabled={disabled}
                        inline={inline}
                        thinLabel={thinLabel}
                        invalid={invalid}
                        htmlFor={id}
                    >
                        {label}
                    </StyledLabelHasHelper>
                    <HelperModal dataId={dataId} modalContent={helperContent}>
                        <QuestionMark />
                    </HelperModal>
                </StyledLabelWrapper>
                <StyledRadio
                    type="radio"
                    data-test-id={`${dataId}-input`}
                    id={id}
                    autoFocus={autofocus}
                    name={name}
                    value={value}
                    disabled={disabled}
                    checked={checked}
                    onChange={onChange}
                    ref={ref}
                />
            </WrapperHasHelper>
        ) : (
            <Wrapper data-test-id={`${dataId}--label`} inline={inline} {...props}>
                <StyledTextLabel inline={inline} thinLabel={thinLabel} invalid={invalid} disabled={disabled}>
                    {label}
                </StyledTextLabel>
                <StyledRadio
                    type="radio"
                    data-test-id={`${dataId}-input`}
                    id={id}
                    autoFocus={autofocus}
                    name={name}
                    value={value}
                    disabled={disabled}
                    checked={checked}
                    onChange={onChange}
                    ref={ref}
                />
            </Wrapper>
        );
    },
);
RadioInput.displayName = 'RadioInput';
